<template>
  <div class="mobtab" dir="rtl">
    <div class="mobtab__inner">
      <!-- Right slot → dashboard (home icon) -->
      <button
        type="button"
        class="mobtab__btn"
        :class="{ 'is-active': isDashboard }"
        aria-label="לוח בקרה"
        @click="goDashboard"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </button>

      <!-- Center FAB → side menu (turns into close button while menu is open) -->
      <button
        type="button"
        class="mobtab__fab"
        :class="{ 'is-active': isMenuOpen }"
        :aria-label="isMenuOpen ? 'סגירה' : 'תפריט'"
        @click="goMenu"
      >
        <span class="mobtab__fab-ring"></span>
        <span class="mobtab__fab-inner">
          <svg
            v-if="isMenuOpen"
            class="mobtab__fab-close"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <img v-else src="/images/sm-logo.svg" alt="SmartCampus" />
        </span>
      </button>

      <!-- Left slot -->
      <button
        type="button"
        class="mobtab__btn"
        aria-label="צ'אט"
        @click="openChat"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TheMobileTabBar',
  computed: {
    isDashboard() {
      const p = this.$route.path;
      return p === '/mobile-dashbord' || p === '/my-dashboard';
    },
    isMenuOpen() {
      return this.$route.path === '/mobile-menu';
    }
  },
  methods: {
    goDashboard() {
      const target =
        this.$store.getters['auth/user'] &&
        this.$store.getters['auth/user'].entity_id === 2
          ? '/my-dashboard'
          : '/mobile-dashbord';
      if (this.$route.path !== target) {
        this.$router.push(target).catch(() => {});
      }
    },
    goMenu() {
      if (this.$route.path === '/mobile-menu') {
        if (window.history.length > 1) {
          this.$router.back();
        } else {
          const fallback =
            this.$store.getters['auth/user'] &&
            this.$store.getters['auth/user'].entity_id === 2
              ? '/my-dashboard'
              : '/mobile-dashbord';
          this.$router.push(fallback).catch(() => {});
        }
        return;
      }
      this.$router.push('/mobile-menu').catch(() => {});
    },
    openChat() {
      try {
        if (window.$crisp && typeof window.$crisp.push === 'function') {
          window.$crisp.push(['do', 'chat:show']);
          window.$crisp.push(['do', 'chat:open']);
        }
      } catch (e) {
        // Crisp unavailable — fail silently.
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.mobtab {
  display: none;
  position: fixed;
  inset: auto 0 0 0;
  // Sit above page content but below modals/drawers (which use z-index ≥ 1000).
  z-index: 90;
  padding: 8px 16px calc(8px + env(safe-area-inset-bottom, 0px));
  font-family: 'Rubik', system-ui, sans-serif;
  pointer-events: none;
}

// Pill-shaped row that hugs the buttons rather than spanning edge-to-edge —
// this matches the redesign target and removes the empty white space the
// stretched bar had with `space-between` distribution.
.mobtab__inner {
  pointer-events: auto;
  margin: 0 auto;
  max-width: 320px;
  width: 100%;
  height: 60px;
  padding: 0 18px;
  background: #fff;
  border: 1px solid rgba(64, 159, 255, 0.12);
  border-radius: 999px;
  box-shadow: 0 12px 28px -14px rgba(15, 18, 32, 0.18),
    0 4px 12px -6px rgba(64, 159, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mobtab__btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: rgba(64, 159, 255, 0.08);
  color: #409fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.12s ease;

  &:active {
    transform: scale(0.94);
  }

  &:hover {
    background: rgba(64, 159, 255, 0.16);
  }

  &.is-active {
    background: linear-gradient(135deg, #409fff, #1f7be5);
    color: #fff;
    box-shadow: 0 6px 16px -8px rgba(64, 159, 255, 0.5);
  }
}

.mobtab__fab {
  position: relative;
  width: 60px;
  height: 60px;
  border: none;
  background: transparent;
  cursor: pointer;
  // Lift the button so it visually overflows the pill.
  transform: translateY(-14px);
  transition: transform 0.18s ease;

  &:active {
    transform: translateY(-12px) scale(0.95);
  }
}

.mobtab__fab-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #409fff;
  box-shadow: 0 10px 22px -6px rgba(64, 159, 255, 0.45),
    0 4px 10px -4px rgba(15, 18, 32, 0.18);
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.mobtab__fab-inner {
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  img {
    width: 38px;
    height: 38px;
    object-fit: contain;
  }
}

.mobtab__fab.is-active {
  .mobtab__fab-ring {
    background: linear-gradient(135deg, #409fff, #1f7be5);
    border-color: transparent;
    box-shadow: 0 14px 26px -8px rgba(64, 159, 255, 0.6);
  }
  .mobtab__fab-inner {
    // Let the colored ring show through, so the logo sits on blue.
    background: transparent;

    img {
      filter: brightness(0) invert(1);
    }
    .mobtab__fab-close {
      color: #fff;
    }
  }
}

.mobtab__fab-close {
  color: #409fff;
}

@media (max-width: 760px) {
  .mobtab {
    display: flex;
  }
}
</style>
